import type { SyntheticEvent } from 'react';
import type { ToggleModalProps } from '../../../components/modal/types';
import type { StudentType } from '../../types';
import type StickerType from '@whizz/api-client/dist/@types/model/Sticker';
import React, { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useIdentity } from '@whizz/react-identity';
import { useI18n } from '@whizz/react-i18n';
import { model } from '@whizz/api-client';
import { Alert, Button, Collapse, Input, Loader, Textarea } from '../../../components';
import { CancelButtonModal, ConfirmButtonModal } from '../../../global';
import { CommonButton, StickerPicker, StudentPicker } from './MessageStudents.components';
import { GLOBAL_ICONS, STATUS } from '../../../util/vars';
import { ModalFooter } from '../../../components/modal/Modal';

const MAX_LENGTH = 255;
const MIN_LABEL_WIDTH = 80;

type MessageSettings = {
  from: string,
  subject: string,
  body: string,
};

export interface MessageStudentsProps extends Pick<ToggleModalProps, 'toggle'> {
  students: StudentType[];
  closeCallback: () => void;
  type?: 'blank' | 'progressions3' | 'progressions1-2' | 'progressions0' | 'hall_of_fame_this_week' | 'not_in_hall_of_fame_this_week' | 'hall_of_fame_last_week' | 'not_in_hall_of_fame_last_week';
}

const MessageStudents = ({ toggle, students, closeCallback, type = 'blank' }: MessageStudentsProps): JSX.Element => {

  const { translate, locale } = useI18n();
  const { me } = useIdentity();

  const [ messageSettings, setMessageSettings ] = useLocalStorage<MessageSettings>(`message_${type}_${me ? me.id : 'unknown'}_${locale?.id ?? 'unknown'}`,
    {
      from: (me && me.first_name ? me.first_name : translate(`pages.HOME.MESSAGE_STUDENTS.${type.toUpperCase()}.FROM`)),
      subject: translate(`pages.HOME.MESSAGE_STUDENTS.${type.toUpperCase()}.SUBJECT`),
      body: translate(`pages.HOME.MESSAGE_STUDENTS.${type.toUpperCase()}.BODY`),
    });

  const [ status, setStatus ] = useState(STATUS.IDLE);
  const [ selectedStudents, setSelectedStudents ] = useState(students);
  const [ selectedSticker, setSelectedSticker ] = useState<StickerType | null>(null);

  const [ showStudents, setShowStudents ] = useState(false);
  const [ showStickers, setShowStickers ] = useState(false);

  const { from, subject, body } = messageSettings || {};

  const isIdle = status === STATUS.IDLE;
  const isSending = status === STATUS.SENDING;
  const isSuccess = status === STATUS.SUCCESS;
  const canSend = !!(selectedStudents.length && from && subject && body);

  const handleChange = (field: string) => (event: SyntheticEvent) => {
    const target = event.target as HTMLInputElement;
    const value = target.value.trimStart();

    if (!(field && messageSettings)) {
      return;
    }
    setMessageSettings({
      ...messageSettings,
      [field]: value,
    });
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    setStatus(STATUS.SENDING);

    const { from, subject, body } = messageSettings || {};
    new model.Message({
      sticker: selectedSticker,
      body: body,
      from: me,
      from_display: from,
      subject: subject,
      to_multiple: selectedStudents.map((student) => new model.Student({ id: student.id })),
    }).create(locale)
      .then(() => {
        setStatus(STATUS.SUCCESS);
      })
      .catch(() => {
        setStatus(STATUS.IDLE); // Error message is displayed by global Error Message
      });
  };

  const removeSticker = () => {
    setSelectedSticker(null);
    setShowStickers(false);
  };

  useEffect(() => {
    setSelectedStudents(students);
  }, [ students ]);

  return (
    <React.Fragment>
      <Alert show={isSuccess} type={isSuccess ? 'success' : 'error'} className={'container mb-md'}>
        <div className={'flex items-center justify-between'}>
          {isSuccess && translate('pages.HOME.MESSAGE_STUDENTS.SENT')}
          <Button
            isOutline
            color={isSuccess ? 'success' : 'error'}
            size={'sm'}
            onClick={() => {
              toggle(false);
              closeCallback();
            }}
          >
            {translate('components.BUTTONS.OK')}
          </Button>
        </div>
      </Alert>
      <form onSubmit={handleSubmit}>
        {isSending && <Loader label={translate('pages.HOME.MESSAGE_STUDENTS.SENDING')} />}
        <Collapse when={isIdle}>
          <div className={'container'}>
            <div className={'mb-md'}>
              <div className={'flex items-center justify-between mb-sm'}>
                <div>
                  <span className={'italic mr-sm'}>{translate('pages.HOME.MESSAGE_STUDENTS.TO')}</span>
                  { selectedStudents.length === students.length
                    ? translate(`pages.HOME.MESSAGE_STUDENTS.${type.toUpperCase()}.TO`)
                    : !selectedStudents.length
                      ? ''
                      : translate(`pages.HOME.MESSAGE_STUDENTS.${type.toUpperCase()}.TO_SELECTED`, { count: selectedStudents.length })
                  }
                </div>
                <CommonButton
                  toggle={showStudents}
                  onClick={() => setShowStudents(!showStudents)}
                >
                  {translate('components.BUTTONS.EDIT')}
                </CommonButton>
              </div>
              <StudentPicker
                open={showStudents}
                students={students}
                selected={selectedStudents}
                setSelected={setSelectedStudents}
              />
            </div>
            <div className={'mb-md'}>
              <Input
                isInline
                id={'from'}
                label={translate('pages.HOME.MESSAGE_STUDENTS.FROM')}
                minWidthForLabel={MIN_LABEL_WIDTH}
                maxLength={MAX_LENGTH}
                value={from}
                onChange={handleChange('from')}
              />
            </div>
            <div className={'mb-md'}>
              <Input
                isInline
                id={'subject'}
                label={translate('pages.HOME.MESSAGE_STUDENTS.SUBJECT')}
                placeholder={translate('pages.HOME.MESSAGE_STUDENTS.PLACEHOLDERS.SUBJECT')}
                minWidthForLabel={MIN_LABEL_WIDTH}
                maxLength={MAX_LENGTH}
                value={subject}
                onChange={handleChange('subject')}
              />
            </div>
            <div className={'mb-md'}>
              <Textarea
                id={'body'}
                label={'Message'}
                placeholder={translate('pages.HOME.MESSAGE_STUDENTS.PLACEHOLDERS.BODY')}
                hideLabel
                maxLength={MAX_LENGTH}
                value={body}
                onChange={handleChange('body')}
              />
            </div>
            <div className={'flex justify-end mb-md'}>
              {selectedSticker ? (
                <CommonButton
                  icon={GLOBAL_ICONS.delete}
                  onClick={removeSticker}
                >
                  {translate('pages.HOME.MESSAGE_STUDENTS.BUTTONS.REMOVE_STICKER')}
                </CommonButton>
              ) : (
                <CommonButton
                  toggle={showStickers}
                  icon={GLOBAL_ICONS.image}
                  onClick={() => setShowStickers(!showStickers)}
                >
                  {translate('pages.HOME.MESSAGE_STUDENTS.BUTTONS.ADD_STICKER')}
                </CommonButton>
              )}
            </div>
            <StickerPicker
              open={showStickers}
              selected={selectedSticker}
              setSelected={setSelectedSticker}
            />
          </div>
        </Collapse>
        <ModalFooter>
          <CancelButtonModal
            onClick={() => {
              toggle(false);
              closeCallback();
            }}
            disabled={isSending}
          />
          <ConfirmButtonModal
            type={'submit'}
            icon={GLOBAL_ICONS.message}
            disabled={!canSend || isSending || isSuccess}
          >
            {translate('pages.HOME.MESSAGE_STUDENTS.BUTTONS.SEND')}
          </ConfirmButtonModal>
        </ModalFooter>
      </form>
    </React.Fragment>
  );
};

export default MessageStudents;
